import React from "react";
import TableProgressBar from "../../TableProgressBar";
import { abbreviateNumber, formatMoneyAsInt } from "../../../utils/format-utils";
import { Header } from "../../../Components";

interface MaxValues {
  reach: number;
  proPurchased: number;
  proPurchaseRate: number;
  proOrderSize: number;
  ntbPurchased: number;
  ntbPurchaseRate: number;
  ntbOrderSize: number;
}

const ProgressBarRenderer = (
  value: number | string,
  maxCompleted: number,
  type: string,
  mediaType: string | null = null
): JSX.Element => {
  const numericValue = typeof value === "string" ? parseFloat(value) : value;
  const numericMax = typeof maxCompleted === "string" ? parseFloat(maxCompleted) : maxCompleted;

  let formatted: string;

  switch (type) {
    case "purchaseRate":
      formatted = `${abbreviateNumber(numericValue)}%`;
      break;
    case "avgOrderSize":
      formatted = formatMoneyAsInt(numericValue);
      break;
    default:
      formatted = abbreviateNumber(numericValue);
  }

  return mediaType === "Grand Total" ? (
    <div>{formatted}</div>
  ) : (
    <TableProgressBar label={formatted} maxCompleted={numericMax} value={numericValue} />
  );
};

interface TableData {
  avgOrderSize: number;
  mediaType: string;
  purchased: number;
  purchaseRate: number;
  reach: number;
}

export const getProspectiveTableHeaders = (maxValues: MaxValues): Header[] => [
  {
    label: "Media Type",
    name: "mediaType",
    flex: 1,
    minFlexWidth: 150,
    renderer: (data: TableData): string => {
      return data.mediaType;
    },
  },
  {
    label: "Shoppers Reached",
    name: "reach",
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: TableData): JSX.Element =>
      ProgressBarRenderer(data.reach, maxValues.reach, "reach", data.mediaType),
  },
  {
    label: "Prospective Purchased",
    name: "purchased",
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: TableData): JSX.Element =>
      ProgressBarRenderer(data.purchased, maxValues.proPurchased, "purchased", data.mediaType),
  },
  {
    label: "Prospective Purchase Rate",
    name: "purchaseRate",
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: TableData): JSX.Element =>
      ProgressBarRenderer(
        data.purchaseRate,
        maxValues.proPurchaseRate,
        "purchaseRate",
        data.mediaType
      ),
  },
  {
    label: "Avg Order Value",
    name: "avgOrderSize",
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: TableData): JSX.Element =>
      ProgressBarRenderer(
        data.avgOrderSize,
        maxValues.proOrderSize,
        "avgOrderSize",
        data.mediaType
      ),
  },
];

export const getNtbTableHeaders = (maxValues: MaxValues): Header[] => [
  {
    label: "Media Type",
    name: "mediaType",
    flex: 1,
    minFlexWidth: 150,
    renderer: (data: TableData): string => {
      return data.mediaType;
    },
  },
  {
    label: "Shoppers Reached",
    name: "reach",
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: TableData): JSX.Element =>
      ProgressBarRenderer(data.reach, maxValues.reach, "reach", data.mediaType),
  },
  {
    label: "NTB Purchased",
    name: "purchased",
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: TableData): JSX.Element =>
      ProgressBarRenderer(data.purchased, maxValues.ntbPurchased, "purchased", data.mediaType),
  },
  {
    label: "NTB Purchase Rate",
    name: "purchaseRate",
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: TableData): JSX.Element =>
      ProgressBarRenderer(
        data.purchaseRate,
        maxValues.ntbPurchaseRate,
        "purchaseRate",
        data.mediaType
      ),
  },
  {
    label: "Avg Order Value",
    name: "avgOrderSize",
    flex: 1,
    minFlexWidth: 105,
    renderer: (data: TableData): JSX.Element =>
      ProgressBarRenderer(
        data.avgOrderSize,
        maxValues.ntbOrderSize,
        "avgOrderSize",
        data.mediaType
      ),
  },
];
